import React, {useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { api } from '../services';
import { getLocalizedPlatformName } from '../utils/platformNames';
import {
  localizeOrderReason,
  localizeOrderStatus,
} from '../utils/localizeOrderReason';

/**
 * Helper function to get the display order ID based on platform
 * @param {Object} order - The order object
 * @returns {string} - Readable order ID (agent) or platform external ID (aggregators)
 */
const getExternalOrderId = (order) => {
  if (!order) return '';
  const platform = String(order.platform || order.update_platform || '').toUpperCase();
  const isAgentFamily =
    order.is_chat_agent_order ||
    platform === 'DIGITAL_CATALOG' ||
    platform === 'CHAT_AGENT';
  if (isAgentFamily) {
    // Prefer human-readable OneHub id (e.g. CHA-260826-225); external is secondary
    return order.order_id || order.external_id || order.jahez_id || order._id || '';
  }
  // Prefer external_id from NEW_ORDER websocket payload, fallback to known IDs
  return order.external_id || order.order_id || order.jahez_id || order._id || '';
};

const getAgentExternalOrderId = (order) => {
  if (!order) return '';
  const platform = String(order.platform || order.update_platform || '').toUpperCase();
  const isAgentFamily =
    order.is_chat_agent_order ||
    platform === 'DIGITAL_CATALOG' ||
    platform === 'CHAT_AGENT';
  if (!isAgentFamily) return '';
  const external =
    order.external_id || order.jahez_id || order.chat_agent_external_id || '';
  if (!external || external === order.order_id) return '';
  return external;
};

/**
 * GlobalOrderNotification Component - Minimal Toast Style
 * Displays a clean, mobile-friendly toast notification when a new order is received
 * 
 * @param {Object} order - The new order data
 * @param {Function} onView - Handler for viewing order details
 * @param {Function} onDismiss - Handler for dismissing notification
 * @param {Boolean} usePortal - If true, renders via Portal to document.body (for normal pages)
 *                               If false, renders directly in component tree (for fullscreen mode)
 */
const GlobalOrderNotification = ({ order, onView, onDismiss, usePortal = true, hideViewButton = false }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.dir() === 'rtl';
  const hasDismissedRef = useRef(false);
  const toastEdgeStyle = isRtl
    ? { left: '20px', right: 'auto' }
    : { right: '20px', left: 'auto' };
  const slideInAnimation = isRtl
    ? 'slideInLeft 0.3s ease-out, fadeOutLeft 0.4s ease-in 4.6s'
    : 'slideInRight 0.3s ease-out, fadeOut 0.4s ease-in 4.6s';
  const currencyLabel = t("common.currencySAR", {
    defaultValue: t("currencySar", { defaultValue: "SAR" }),
  });
  const sharedToastAnimStyle = `
    @keyframes slideInRight {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideInLeft {
      from { transform: translateX(-100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes fadeOut {
      to { opacity: 0; transform: translateX(50%); }
    }
    @keyframes fadeOutLeft {
      to { opacity: 0; transform: translateX(-50%); }
    }
  `;

  useEffect(() => {
    if (order?.is_timeout && !hasDismissedRef.current) {
      hasDismissedRef.current = true;
      // Clear state after a longer delay to ensure rendering completes
      setTimeout(() => {
        onDismiss();
      }, 4500); // 100ms delay instead of 0
    }
  }, [order?.is_timeout, onDismiss]);

  useEffect(() => {
    if (order?.is_order_updated && !hasDismissedRef.current) {
      hasDismissedRef.current = true;
      setTimeout(() => {
        onDismiss();
      }, 4500);
    }
  }, [order?.is_order_updated, onDismiss]);

  // If this is an inventory warning, trigger existing toast and exit
  useEffect(() => {
    if (order?.is_inventory_warning) {
      const platform = order.platform || t('platform', { defaultValue: 'Platform' });
      const storeId = order.store_id || '';
      const rawType = (order.warning_type || 'INVENTORY_WARNING');
      const warningType = rawType.replace(/_/g, ' ');
      const products = Array.isArray(order.products) ? order.products : [];
      const totalProducts = order.total_products ?? products.length;
      const unavailableCount = order.unavailable_count ?? products.filter(p => (p.status || '').toUpperCase() === 'OUT_OF_STOCK').length;
      const isError = warningType.toUpperCase().includes('OUT OF STOCK') || unavailableCount > 0;
      const toastId = `inventory-warning-${storeId || '-'}-${rawType.toUpperCase()}`;

      const resolveStoreNameAndNotify = async () => {
        let storeName = order.store_name || (order.store && order.store.name) || '';
        if (!storeName && storeId) {
          try {
            const token = localStorage.getItem('token');
            const res = await api.get(`/stores/${storeId}`, { headers: { Authorization: `Bearer ${token}` } });
            storeName = res.data?.name || res.data?.store?.name || '';
          } catch (_) {
            storeName = '';
          }
        }

        const displayStore = storeName || t("orderNotification.yourStore");
        const platformLabel = getLocalizedPlatformName(platform, t) || platform;
        
        // Get specific product names for out of stock and low stock items
        const outOfStockItems = products.filter(p => {
          const status = (p.status || '').toUpperCase();
          return status === 'OUT_OF_STOCK' || status === 'OUT_OF_STOCK_WARNING';
        });
        const lowStockItems = products.filter(p => {
          const status = (p.status || '').toUpperCase();
          return status === 'LOW_STOCK' || status === 'LOW_STOCK_WARNING';
        });
        
        // Show separate toasts for different severity levels
        if (outOfStockItems.length > 0) {
          const itemNames = outOfStockItems.map(p => p.product_name || p.name || t("orderNotification.unknownItem")).join(', ');
          const outOfStockMessage = t("orderNotification.outOfStockAt", { store: displayStore, items: itemNames });
          toast.error(`${platformLabel}: ${outOfStockMessage}`, { 
            autoClose: 8000, 
            closeOnClick: false, 
            toastId: `${toastId}-out-of-stock` 
          });
        }
        
        if (lowStockItems.length > 0) {
          const itemNames = lowStockItems.map(p => p.product_name || p.name || t("orderNotification.unknownItem")).join(', ');
          const lowStockMessage = t("orderNotification.lowStockAt", { store: displayStore, items: itemNames });
          toast.warn(`${platformLabel}: ${lowStockMessage}`, { 
            autoClose: 8000, 
            closeOnClick: false, 
            toastId: `${toastId}-low-stock` 
          });
        }
        
        // Fallback only when no items have recognizable status
        if (outOfStockItems.length === 0 && lowStockItems.length === 0) {
          const message = isError
            ? t("orderNotification.someOutOfStockAt", { store: displayStore, count: unavailableCount })
            : t("orderNotification.lowStockSummary", { store: displayStore, count: totalProducts });
          
          const fn = isError ? toast.error : toast.warn;
          fn(`${platformLabel}: ${message}`, { autoClose: 8000, closeOnClick: false, toastId });
        }
        onDismiss();
      };

      resolveStoreNameAndNotify();
    }
  }, [order, onDismiss, t]);
  // For timeout orders, render simple toast instead of popup
  if (order?.is_timeout) {
    return (
      <div
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{
        position: "fixed",
        top: "80px",
        ...toastEdgeStyle,
        zIndex: 10000,
        backgroundColor: "#fff",
        color: "#333",
        padding: "14px 18px",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        fontSize: "14px",
        fontWeight: "500",
        minWidth: "280px",
        animation: slideInAnimation,
      }}
    >
      {/* Red circle icon */}
      <div
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: "#dc3545",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        <span style={{ color: "#fff", fontSize: "12px" }}>⏰</span>
      </div>

      {/* Message */}
      <div style={{ lineHeight: "1.4" }}>
        {t("orderNotification.timedOut", { id: getExternalOrderId(order) })}
        {order.timeout_reason ? (
          <div style={{ color: "#6c757d", fontSize: "12px", marginTop: "4px" }}>
            {localizeOrderReason(order.timeout_reason, t)}
          </div>
        ) : null}
      </div>

      {/* Animations */}
      <style>{sharedToastAnimStyle}</style>
    </div>
    );
  }

  if (order?.is_order_updated) {
    const updatedStatus = localizeOrderStatus(
      order.new_status || order.status || order.loops_status,
      t
    );
    const updatedReason = localizeOrderReason(order.update_reason, t);
    const platformLabel = getLocalizedPlatformName(
      order.update_platform || order.platform,
      t
    );

    return (
      <div
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{
        position: "fixed",
        top: "80px",
        ...toastEdgeStyle,
        zIndex: 10000,
        backgroundColor: "#fff",
        color: "#333",
        padding: "14px 18px",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        fontSize: "14px",
        fontWeight: "500",
        minWidth: "280px",
        animation: slideInAnimation,
      }}
    >
      <div
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: "#0d6efd",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        <span style={{ color: "#fff", fontSize: "12px" }}>ℹ️</span>
      </div>

      <div style={{ lineHeight: "1.4" }}>
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>
          {t("orderNotification.orderUpdated", {
            defaultValue: t("Order Updated", { defaultValue: "Order Updated" }),
          })}
          {platformLabel ? ` · ${platformLabel}` : ""}
        </div>
        <div>
          {t("orderId")} # {getExternalOrderId(order)}
          {updatedReason ? <>, {updatedReason}</> : null}
        </div>
        {updatedStatus ? (
          <div style={{ color: "#6c757d", fontSize: "12px", marginTop: "4px" }}>
            {t("status")}: {updatedStatus}
          </div>
        ) : null}
      </div>

      <style>{sharedToastAnimStyle}</style>
    </div>
    );
  }

  // Inventory warnings use existing toast code; do not render a custom UI
  if (order?.is_inventory_warning) {
    return null;
  }

  const notificationContent = (
    <>
      {/* Toast Notification - Top Right, Minimal Design */}
      <div
        onClick={onView}
        dir={isRtl ? 'rtl' : 'ltr'}
        style={{
          position: 'fixed',
          top: '20px',
          ...toastEdgeStyle,
          zIndex: 999999,
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          padding: '16px 20px',
          minWidth: '320px',
          maxWidth: '400px',
          animation: isRtl
            ? 'slideInLeft 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
            : 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          border: '1px solid #e9ecef',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        className={`global-toast-notification${isRtl ? ' is-rtl' : ''}`}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.15)';
        }}
      >
        {/* Header Row */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          {/* Icon + Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              backgroundColor: '#073b4c',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0
            }}>
              🔔
            </div>
            <div>
              <h5 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: '#000',
                lineHeight: '1.2'
              }}>
                {t("New Order Received")}
              </h5>
              <p style={{
                margin: '2px 0 0 0',
                fontSize: '12px',
                color: '#6c757d',
                lineHeight: '1.2'
              }}>
                {getLocalizedPlatformName(order.platform, t) || t("orderNotification.customer")}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              width: '24px',
              height: '24px',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              fontWeight: '300',
              flexShrink: 0,
              marginInlineStart: '8px',
              padding: 0
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.color = '#000';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }}
          >
            ×
          </button>
        </div>

        {/* Order Details - Minimal */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px 12px',
          borderRadius: '8px',
          marginBottom: '10px',
          fontSize: '13px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '4px',
            color: '#495057'
          }}>
            <span style={{ fontWeight: '500', color: '#6c757d' }}>{t("orderId")}</span>
            <span style={{ 
              fontFamily: 'monospace', 
              color: '#073b4c',
              fontWeight: '600',
              fontSize: '12px' 
            }}>
              {getExternalOrderId(order)}
            </span>
          </div>

          {getAgentExternalOrderId(order) ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '4px',
              color: '#495057'
            }}>
              <span style={{ fontWeight: '500', color: '#6c757d' }}>{t("External ID")}</span>
              <span style={{ 
                fontFamily: 'monospace', 
                color: '#6c757d',
                fontWeight: '500',
                fontSize: '12px' 
              }}>
                {getAgentExternalOrderId(order)}
              </span>
            </div>
          ) : null}
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '6px',
            borderTop: '1px solid #dee2e6',
            marginTop: '4px'
          }}>
            <span style={{ fontWeight: '600', fontSize: '14px', color: '#000' }}>{t("total", { defaultValue: "Total" })}</span>
            <span style={{ 
              fontWeight: '700', 
              fontSize: '16px', 
              color: '#28a745'
            }}>
              {order.final_price || order.total_price || 0} {currencyLabel}
            </span>
          </div>
        </div>

        {/* Action Button (Hidden on OrdersDisplay page) */}
        {!hideViewButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            style={{
              width: '100%',
              backgroundColor: '#073b4c',
              color: '#fff',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#05303e';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#073b4c';
            }}
          >
            <span style={{ fontSize: '16px' }}>👁️</span>
            <span>{t("View Full Details")}</span>
          </button>
        )}

        {/* Subtle Progress Bar - 10 second countdown */}
        <div style={{
          marginTop: '10px',
          height: '2px',
          backgroundColor: '#e9ecef',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            backgroundColor: '#073b4c',
            animation: 'shrink 10s linear forwards'
          }} />
        </div>
      </div>

      {/* Animations */}
      <style>{`
        ${sharedToastAnimStyle}

        @keyframes slideInRight {
          0% {
            transform: translateX(450px);
            opacity: 0;
          }
          70% {
            transform: translateX(-10px);
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideInLeft {
          0% {
            transform: translateX(-450px);
            opacity: 0;
          }
          70% {
            transform: translateX(10px);
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .global-toast-notification {
            left: 10px !important;
            right: 10px !important;
            top: 10px !important;
            min-width: unset !important;
            max-width: unset !important;
            padding: 14px 16px !important;
          }
        }

        @media (max-width: 360px) {
          .global-toast-notification {
            padding: 12px 14px !important;
          }
          .global-toast-notification h5 {
            font-size: 14px !important;
          }
          .global-toast-notification button {
            padding: 8px 14px !important;
            font-size: 13px !important;
          }
        }

        /* Tablet / Desktop — keep start-edge placement */
        @media (min-width: 481px) {
          .global-toast-notification:not(.is-rtl) {
            right: 20px !important;
            left: auto !important;
            max-width: 400px !important;
          }
          .global-toast-notification.is-rtl {
            left: 20px !important;
            right: auto !important;
            max-width: 400px !important;
          }
        }

        /* Hover effect for whole toast */
        .global-toast-notification:hover {
          border-color: #073b4c;
        }
      `}</style>
    </>
  );
  
  // Use portal to document.body by default, or render directly when usePortal is false
  return usePortal ? ReactDOM.createPortal(notificationContent, document.body) : notificationContent;
};

export default GlobalOrderNotification;
